import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Bell, Loader2, Mail, Settings, History, FileText, Cake, Clock,
  Calendar, Check, X, Users, Send, Save, Trash2, Plus, AlertTriangle
} from "lucide-react";

const TIPOS_SOLICITUD = [
  { id: "permiso", label: "Permisos", icon: FileText, color: "bg-blue-500" },
  { id: "cumpleanos", label: "Día de Cumpleaños", icon: Cake, color: "bg-pink-500" },
  { id: "tardanza", label: "Tardanza/Ausencia", icon: Clock, color: "bg-amber-500" },
  { id: "vacaciones", label: "Vacaciones", icon: Calendar, color: "bg-green-500" }
];

const DESTINATARIOS_OPCIONES = [
  { id: "supervisor", label: "Supervisor del empleado", description: "El supervisor directo del solicitante" },
  { id: "rrhh", label: "Gestión Humana", description: "Todo el equipo de RRHH" },
  { id: "admin", label: "Administradores", description: "Todos los administradores del sistema" },
  { id: "solicitante", label: "Solicitante", description: "El empleado que hace la solicitud" },
  { id: "custom", label: "Correos personalizados", description: "Agregar correos específicos" }
];

export default function AlertasSolicitudesPage() {
  const [configs, setConfigs] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("configuracion");
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "gestion_humana"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [configsRes, historialRes] = await Promise.all([
        axios.get(`${API}/alertas-solicitudes/config`, getAuthHeaders()),
        axios.get(`${API}/alertas-solicitudes/historial?limit=50`, getAuthHeaders())
      ]);
      setConfigs(configsRes.data);
      setHistorial(historialRes.data.alertas || []);
    } catch (err) {
      toast.error("Error al cargar configuración de alertas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveConfig = async () => {
    if (!selectedConfig) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/alertas-solicitudes/config/${selectedConfig.tipo_solicitud}`,
        selectedConfig,
        getAuthHeaders()
      );
      toast.success("Configuración guardada");
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (config) => {
    setSelectedConfig({ ...config });
    setShowEditDialog(true);
  };

  const toggleDestinatario = (destId) => {
    if (!selectedConfig) return;
    const current = selectedConfig.destinatarios_tipo || [];
    const updated = current.includes(destId)
      ? current.filter(d => d !== destId)
      : [...current, destId];
    setSelectedConfig({ ...selectedConfig, destinatarios_tipo: updated });
  };

  const addCustomEmail = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Ingrese un correo válido");
      return;
    }
    const current = selectedConfig?.emails_personalizados || [];
    if (current.includes(newEmail)) {
      toast.error("Este correo ya está agregado");
      return;
    }
    setSelectedConfig({
      ...selectedConfig,
      emails_personalizados: [...current, newEmail]
    });
    setNewEmail("");
  };

  const removeCustomEmail = (email) => {
    const current = selectedConfig?.emails_personalizados || [];
    setSelectedConfig({
      ...selectedConfig,
      emails_personalizados: current.filter(e => e !== email)
    });
  };

  const getTipoIcon = (tipo) => {
    const t = TIPOS_SOLICITUD.find(ts => ts.id === tipo);
    return t?.icon || Bell;
  };

  const getTipoColor = (tipo) => {
    const t = TIPOS_SOLICITUD.find(ts => ts.id === tipo);
    return t?.color || "bg-gray-500";
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <Bell className="w-8 h-8 text-purple-600" />
              Alertas de Solicitudes
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Configura quién recibe alertas cuando se crean solicitudes
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white dark:bg-slate-800 border dark:border-slate-700 mb-6">
              <TabsTrigger value="configuracion" className="gap-2">
                <Settings className="w-4 h-4" />
                Configuración
              </TabsTrigger>
              <TabsTrigger value="historial" className="gap-2">
                <History className="w-4 h-4" />
                Historial de Alertas
              </TabsTrigger>
            </TabsList>

            {/* Configuración Tab */}
            <TabsContent value="configuracion">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {configs.map(config => {
                    const Icon = getTipoIcon(config.tipo_solicitud);
                    return (
                      <Card 
                        key={config.tipo_solicitud}
                        className="dark:bg-slate-800 dark:border-slate-700"
                      >
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-3 rounded-xl ${getTipoColor(config.tipo_solicitud)}`}>
                                <Icon className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <CardTitle className="text-lg">{config.nombre_tipo}</CardTitle>
                                <CardDescription>
                                  {config.destinatarios_tipo?.length || 0} destinatarios configurados
                                </CardDescription>
                              </div>
                            </div>
                            <Badge className={config.alertas_activas ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                              {config.alertas_activas ? "Activo" : "Inactivo"}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {/* Status indicators */}
                            <div className="flex flex-wrap gap-2">
                              {config.enviar_email && (
                                <Badge variant="outline" className="gap-1">
                                  <Mail className="w-3 h-3" />
                                  Email
                                </Badge>
                              )}
                              {config.generar_notificacion && (
                                <Badge variant="outline" className="gap-1">
                                  <Bell className="w-3 h-3" />
                                  Notificación
                                </Badge>
                              )}
                            </div>

                            {/* Destinatarios */}
                            <div>
                              <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Destinatarios:</p>
                              <div className="flex flex-wrap gap-1">
                                {config.destinatarios_tipo?.map(dest => (
                                  <Badge key={dest} className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                    {DESTINATARIOS_OPCIONES.find(d => d.id === dest)?.label || dest}
                                  </Badge>
                                ))}
                                {config.emails_personalizados?.length > 0 && (
                                  <Badge className="bg-blue-100 text-blue-700">
                                    +{config.emails_personalizados.length} emails
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Edit button */}
                            {isAdmin && (
                              <Button 
                                onClick={() => openEditDialog(config)}
                                variant="outline"
                                className="w-full gap-2"
                              >
                                <Settings className="w-4 h-4" />
                                Configurar
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Historial Tab */}
            <TabsContent value="historial">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle>Historial de Alertas Enviadas</CardTitle>
                  <CardDescription>
                    Últimas 50 alertas generadas por el sistema
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {historial.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No hay alertas registradas
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-900">
                          <tr>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Tipo</th>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Solicitante</th>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Asunto</th>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Destinatarios</th>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Estado</th>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Fecha</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {historial.map(alerta => {
                            const Icon = getTipoIcon(alerta.tipo_solicitud);
                            return (
                              <tr key={alerta.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded ${getTipoColor(alerta.tipo_solicitud)}`}>
                                      <Icon className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-sm capitalize">{alerta.tipo_solicitud}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-slate-900 dark:text-white">
                                  {alerta.solicitante_nombre}
                                </td>
                                <td className="p-3 text-slate-600 dark:text-slate-300 max-w-xs truncate">
                                  {alerta.asunto}
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline">
                                    {alerta.destinatarios?.length || 0} destinatarios
                                  </Badge>
                                </td>
                                <td className="p-3">
                                  <Badge className={
                                    alerta.estado === "enviada" 
                                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                      : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                  }>
                                    {alerta.email_enviado ? (
                                      <><Check className="w-3 h-3 mr-1" /> Email enviado</>
                                    ) : alerta.notificacion_generada ? (
                                      <><Bell className="w-3 h-3 mr-1" /> Notificación</>
                                    ) : (
                                      <><X className="w-3 h-3 mr-1" /> Fallida</>
                                    )}
                                  </Badge>
                                </td>
                                <td className="p-3 text-sm text-slate-500">
                                  {new Date(alerta.created_at).toLocaleString('es', { 
                                    day: '2-digit', 
                                    month: 'short', 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-purple-500" />
                Configurar Alertas - {selectedConfig?.nombre_tipo}
              </DialogTitle>
            </DialogHeader>
            
            {selectedConfig && (
              <div className="space-y-6">
                {/* Main toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Alertas Activas</Label>
                    <p className="text-sm text-slate-500">Habilitar alertas para este tipo de solicitud</p>
                  </div>
                  <Switch
                    checked={selectedConfig.alertas_activas}
                    onCheckedChange={(v) => setSelectedConfig({...selectedConfig, alertas_activas: v})}
                  />
                </div>

                {/* Notification methods */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Métodos de Notificación</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg dark:border-slate-600">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <span>Enviar Email</span>
                      </div>
                      <Switch
                        checked={selectedConfig.enviar_email}
                        onCheckedChange={(v) => setSelectedConfig({...selectedConfig, enviar_email: v})}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg dark:border-slate-600">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-purple-500" />
                        <span>Notificación Interna</span>
                      </div>
                      <Switch
                        checked={selectedConfig.generar_notificacion}
                        onCheckedChange={(v) => setSelectedConfig({...selectedConfig, generar_notificacion: v})}
                      />
                    </div>
                  </div>
                </div>

                {/* Recipients */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Destinatarios</Label>
                  <div className="space-y-2">
                    {DESTINATARIOS_OPCIONES.map(dest => (
                      <div 
                        key={dest.id}
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedConfig.destinatarios_tipo?.includes(dest.id)
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'dark:border-slate-600 hover:border-purple-300'
                        }`}
                        onClick={() => toggleDestinatario(dest.id)}
                      >
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{dest.label}</p>
                          <p className="text-sm text-slate-500">{dest.description}</p>
                        </div>
                        <Checkbox 
                          checked={selectedConfig.destinatarios_tipo?.includes(dest.id)}
                          onCheckedChange={() => toggleDestinatario(dest.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom emails */}
                {selectedConfig.destinatarios_tipo?.includes("custom") && (
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Correos Personalizados</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="correo@ejemplo.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="flex-1 dark:bg-slate-700 dark:border-slate-600"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomEmail())}
                      />
                      <Button onClick={addCustomEmail} size="icon">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {selectedConfig.emails_personalizados?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedConfig.emails_personalizados.map(email => (
                          <Badge key={email} variant="secondary" className="gap-1 pr-1">
                            {email}
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-4 w-4 hover:bg-red-100"
                              onClick={() => removeCustomEmail(email)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Email template */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Plantilla de Email</Label>
                  <div>
                    <Label className="text-sm">Asunto</Label>
                    <Input
                      value={selectedConfig.plantilla_asunto || ""}
                      onChange={(e) => setSelectedConfig({...selectedConfig, plantilla_asunto: e.target.value})}
                      placeholder="Nueva solicitud de {tipo} - {solicitante}"
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Variables: {"{solicitante}"}, {"{departamento}"}, {"{tipo}"}, {"{fecha_inicio}"}, {"{fecha_fin}"}, {"{dias}"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm">Mensaje</Label>
                    <Textarea
                      value={selectedConfig.plantilla_mensaje || ""}
                      onChange={(e) => setSelectedConfig({...selectedConfig, plantilla_mensaje: e.target.value})}
                      placeholder="El empleado {solicitante} ha realizado una solicitud..."
                      className="dark:bg-slate-700 dark:border-slate-600 min-h-[100px]"
                    />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveConfig} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Configuración
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
